/**
 * MAP 249's lists for shop names and email content (spec §4, §4b). ServWell tunes them by a code
 * change, here and nowhere else. The matching itself is server-side (`apps/api/src/lib/
 * shop-name.ts` and `lib/email-content.ts`): it compares Unicode skeletons (UTS #39), whose
 * confusables table is too big for the web bundle.
 *
 * Every entry is matched on its skeleton, so "Geek_Squad", "P a y P a l" and a Cyrillic
 * "Ѕervwell" are the same words as the plain spellings below.
 */

/** A shop's first days (spec §4b): its customer email is checked more closely. */
export const NEW_SHOP_DAYS = 14;

/**
 * Held whenever a customer email's shop-written text names one (spec §4b, "always held"): the
 * brands callback scams pose as, which a trades email never names.
 */
export const ALWAYS_HELD_WORDS = [
  'Geek Squad',
  'Norton',
  'McAfee',
  'LifeLock',
  'Symantec',
  'Microsoft',
  'Coinbase',
  'Binance',
  'IRS',
  'Social Security',
  'ServWell support',
] as const;

/**
 * Held only in a shop's first `NEW_SHOP_DAYS` (spec §4b, "new-shop list"): common in scams, but
 * also in a real shop's mail ("you can pay by Zelle", "the part ships from Amazon").
 */
export const NEW_SHOP_HELD_WORDS = [
  'PayPal',
  'Venmo',
  'Zelle',
  'Cash App',
  'Amazon',
  'Apple',
  'Best Buy',
  'eBay',
  'Wells Fargo',
  'Chase',
  'Bank of America',
  'Citi',
  'USPS',
  'FedEx',
] as const;

/** The longest a shop name may be (spec §4). */
export const MAX_SHOP_NAME = 80;

/**
 * How a brand on the shop-name list matches a name (spec §4's brand hold):
 *  - `anywhere`: a coined name no trade business carries. It matches anywhere in the name, even
 *    run into other words ("PaypalSecurity").
 *  - `word`: a short one. It matches as a whole word, or letters spaced out ("I.R.S."), but never
 *    inside another word ("First Choice" is not the IRS).
 *  - `posing`: an ordinary word or surname as well as a brand ("Apple Valley Plumbing", "Chase
 *    Plumbing"). It matches only when the name POSES as the brand: the brand alone, with company
 *    words only ("Apple Inc."), or with a word like support, security or billing
 *    (`POSING_WORDS`).
 */
export type ShopNameBrandMatch = 'anywhere' | 'word' | 'posing';

/**
 * The brand list for shop names (spec §4): a match SAVES the name, but the shop's customer email
 * is held until staff clear that exact name. These are the most-impersonated brands, the
 * callback-scam brands, and banks, the IRS and USPS (R2 §4).
 */
export const SHOP_NAME_BRANDS: readonly {
  brand: string;
  match: ShopNameBrandMatch;
}[] = [
  { brand: 'Microsoft', match: 'anywhere' },
  { brand: 'LinkedIn', match: 'anywhere' },
  { brand: 'Google', match: 'anywhere' },
  { brand: 'Apple', match: 'posing' },
  { brand: 'Amazon', match: 'posing' },
  { brand: 'Adobe', match: 'posing' },
  { brand: 'Facebook', match: 'anywhere' },
  { brand: 'WhatsApp', match: 'anywhere' },
  { brand: 'PayPal', match: 'anywhere' },
  { brand: 'OpenAI', match: 'word' },
  { brand: 'Geek Squad', match: 'anywhere' },
  { brand: 'Best Buy', match: 'anywhere' },
  { brand: 'Norton', match: 'posing' },
  { brand: 'McAfee', match: 'anywhere' },
  { brand: 'LifeLock', match: 'anywhere' },
  { brand: 'eBay', match: 'word' },
  { brand: 'Symantec', match: 'anywhere' },
  { brand: 'IRS', match: 'word' },
  { brand: 'USPS', match: 'word' },
  { brand: 'Bank of America', match: 'anywhere' },
  { brand: 'Chase', match: 'posing' },
  { brand: 'Wells Fargo', match: 'anywhere' },
  { brand: 'Citi', match: 'word' },
];

/**
 * Words that make a name pose as a `posing` brand: "Apple Support", "Chase Bank", "Norton
 * Security". Trade words (repair, service, plumbing) are deliberately not here.
 */
export const POSING_WORDS = [
  'support',
  'security',
  'secure',
  'help',
  'helpdesk',
  'helpline',
  'desk',
  'billing',
  'bill',
  'bills',
  'payment',
  'payments',
  'pay',
  'account',
  'accounts',
  'login',
  'signin',
  'verify',
  'verification',
  'verified',
  'id',
  'refund',
  'refunds',
  'renewal',
  'renew',
  'subscription',
  'subscriptions',
  'protection',
  'antivirus',
  'virus',
  'fraud',
  'alert',
  'alerts',
  'notice',
  'official',
  'customer',
  'customers',
  'care',
  'team',
  'department',
  'dept',
  'center',
  'centre',
  'store',
  'bank',
  'banking',
  'card',
  'cards',
  'credit',
  'member',
  'members',
  'tech',
  'technical',
  'cloud',
  'icloud',
  'prime',
  'online',
] as const;

/** Company words: a `posing` brand with only these beside it IS the brand ("Apple Inc."). */
export const NAME_FILLER_WORDS = [
  'the',
  'of',
  'and',
  'a',
  'an',
  'inc',
  'incorporated',
  'llc',
  'ltd',
  'limited',
  'co',
  'corp',
  'corporation',
  'company',
  'group',
  'usa',
  'us',
  'america',
  'international',
  'global',
  'plc',
] as const;

/**
 * Why an email ServWell staff rejected after holding it for a look did not go (`skipped /
 * rejected`, spec §4b), as a fact with no full stop: the reminder and follow-up ledgers' `detail`
 * and the ticket panel's line say it in these words. Not the mail service's refusal, which is
 * `failed / rejected` ("The mail service turned it down").
 */
export const STAFF_REJECTED_DETAIL = 'ServWell checked this email and stopped it';

/** What a detail page says of an email ServWell staff rejected (`skipped / rejected`). */
export const CUSTOMER_EMAIL_STAFF_REJECTED_LINE = `Didn’t send: ${STAFF_REJECTED_DETAIL}. Contact ServWell if you think that’s wrong.`;
